import {Attributes, AttributesRow, Card, DataStatus, Flag} from 'platform/components';
import {Box, Heading, HStack, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {defaultTo, find, isNil, propEq} from 'ramda';

import {CoreExchangeItemsTab, useGetWarehousesQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {getCustomerName, useGetSupplierCustomersQuery, useTenant} from '@dms/shared';
import {getSupplierById} from '@dms/warehouse/shared';

import {EMPTY_PLACEHOLDER, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CURRENCY_DECIMALS} from '../../../../../constants/CURRENCY_DECIMALS';
import {getCoreItemStateFlag} from '../../../../../utils/getCoreItemStateFlag';
import {CoreExchangeItemCard} from './CoreExchangeItemCard';

interface ItemsTabProps extends RequiredTestIdProps {
  items: CoreExchangeItemsTab | Nullish;
  onEditGeneral: VoidFunction;
  isEditable: boolean;
}

export function ItemsTab(props: ItemsTabProps) {
  const tenant = useTenant();
  const formatCurrency = useFormatCurrency();

  const {data: warehouses, isLoading: areWarehousesLoading} = useGetWarehousesQuery();
  const {data: suppliers, isLoading: areSuppliersLoading} = useGetSupplierCustomersQuery();

  const items = defaultTo([], props.items?.items);
  const mainItem = find(propEq('MAIN', 'itemType'), items);
  const coreItem = find(propEq('CORE', 'itemType'), items);
  const coreItemStateFlag = getCoreItemStateFlag(coreItem?.coreItemState);

  const formatTotal = (value: number | Nullish) => {
    if (isNil(value)) {
      return EMPTY_PLACEHOLDER;
    }
    return formatCurrency(value, tenant.tenantCurrency, CURRENCY_DECIMALS);
  };

  const returnCoreItemToSupplier = props.items?.general?.returnCoreItemToSupplier;

  const generalRows: AttributesRow[] = [
    {
      label: i18n.t('entity.condition.labels.generalNote'),
      value: props.items?.general?.generalNote,
    },
    {
      label: i18n.t('entity.warehouse.labels.coreExchangeReturnCoreItemToSupplier'),
      content: isNil(returnCoreItemToSupplier) ? (
        EMPTY_PLACEHOLDER
      ) : (
        <Flag
          isSubtle={!returnCoreItemToSupplier}
          colorScheme={returnCoreItemToSupplier ? 'green' : 'neutral'}
          label={i18n.t(returnCoreItemToSupplier ? 'general.labels.yes' : 'general.labels.no')}
          size="small"
          data-testid={suffixTestId('returnCoreItemToSupplier', props)}
        />
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.supplier'),
      value: getCustomerName(
        getSupplierById(defaultTo([], suppliers), props.items?.general?.supplierId)
      ),
    },
  ];

  return (
    <DataStatus isLoading={areWarehousesLoading || areSuppliersLoading} minHeight={50}>
      <VStack spacing={4}>
        <Card>
          <HStack spacing={8}>
            <VStack spacing={1}>
              <Text size="xSmall" color="tertiary">
                {i18n.t('entity.warehouse.labels.coreExchangeTotalExclCoreChargeWithoutVat')}
              </Text>
              <Heading size={3} data-testid={suffixTestId('totalWithoutVat', props)}>
                {formatTotal(props.items?.totalValueExclCoreChargeWithoutVat)}
              </Heading>
            </VStack>
            <VStack spacing={1}>
              <Text size="xSmall" color="tertiary">
                {i18n.t('entity.warehouse.labels.coreExchangeTotalExclCoreChargeWithVat')}
              </Text>
              <Heading size={3} data-testid={suffixTestId('totalWithVat', props)}>
                {formatTotal(props.items?.totalValueExclCoreChargeWithVat)}
              </Heading>
            </VStack>
          </HStack>
        </Card>
        <Card
          title={i18n.t('entity.warehouse.labels.general')}
          actions={[
            {
              type: 'button',
              variant: 'link',
              leftIcon: 'image/edit',
              title: i18n.t('general.actions.edit'),
              onClick: props.onEditGeneral,
              isDisabled: !props.isEditable,
              tooltip: props.isEditable ? null : i18n.t('general.labels.noPermission'),
              'data-testid': suffixTestId('general.edit', props),
            },
          ]}
        >
          <Attributes
            rows={generalRows}
            size="third"
            data-testid={suffixTestId('generalAttributes', props)}
          />
        </Card>
        <Card>
          <HStack spacing={4}>
            <Box flex={1}>
              <CoreExchangeItemCard
                title={i18n.t('entity.warehouse.labels.coreExchangeMainItem')}
                titleIcon="action/settings"
                item={mainItem}
                warehouses={warehouses}
                suppliers={suppliers}
                totalPriceLabel={i18n.t(
                  'entity.warehouse.labels.coreExchangeTotalPriceInclCoreCharge'
                )}
                data-testid={suffixTestId('mainItem', props)}
              />
            </Box>
            <Box flex={1}>
              <CoreExchangeItemCard
                title={i18n.t('entity.warehouse.labels.coreExchangeCoreItem')}
                titleIcon="action/autorenew"
                item={coreItem}
                warehouses={warehouses}
                suppliers={suppliers}
                totalPriceLabel={i18n.t('entity.warehouse.labels.coreExchangeCoreCharge')}
                flags={isNil(coreItemStateFlag) ? undefined : [coreItemStateFlag]}
                data-testid={suffixTestId('coreItem', props)}
              />
            </Box>
          </HStack>
        </Card>
      </VStack>
    </DataStatus>
  );
}
